import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, teerTodayDisplay } from "../lib/api";
import { GAMES } from "../lib/games";
import Layout from "../components/Layout";
import Seo from "../components/Seo";
import { Calendar, ChevronDown } from "lucide-react";

const FR_TIME = "11:10 PM";
const SR_TIME = "12:10 AM";

function TodayResultCard({ date, result, loading }) {
  return (
    <div
      data-testid="today-result-card"
      className="bg-white rounded-2xl border border-slate-200 shadow-[0_8px_30px_rgb(0,0,0,0.08)] overflow-hidden"
    >
      {/* Red header bar */}
      <div className="flex items-center justify-between gap-3 bg-red-600 px-4 sm:px-5 py-3">
        <div className="flex items-center gap-2 text-white font-semibold text-sm sm:text-base" data-testid="today-result-date">
          <Calendar size={18} className="shrink-0" />
          <span>Date: {date}</span>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-bold text-red-600">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75"></span>
            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600"></span>
          </span>
          Live Result
        </span>
      </div>

      {/* Market name */}
      <div className="bg-slate-900 py-2.5 text-center">
        <span className="font-oswald text-lg sm:text-xl font-bold tracking-wide text-white">Shillong Night</span>
      </div>

      {/* Timings row */}
      <div className="grid grid-cols-2 divide-x divide-slate-200 border-b border-slate-200 bg-slate-50">
        <div className="py-3 text-center text-sm sm:text-base font-semibold text-slate-700">
          F/R <span className="text-slate-400">·</span> {FR_TIME}
        </div>
        <div className="py-3 text-center text-sm sm:text-base font-semibold text-slate-700">
          S/R <span className="text-slate-400">·</span> {SR_TIME}
        </div>
      </div>

      {/* Results row */}
      <div className="grid grid-cols-2 divide-x divide-slate-200">
        <div className="flex flex-col items-center py-7">
          <span data-testid="today-fr-number" className="font-oswald font-bold leading-none text-red-600 text-6xl sm:text-7xl tracking-tighter">
            {result?.fr || "XX"}
          </span>
        </div>
        <div className="flex flex-col items-center py-7">
          <span data-testid="today-sr-number" className="font-oswald font-bold leading-none text-red-600 text-6xl sm:text-7xl tracking-tighter">
            {result?.sr || "XX"}
          </span>
        </div>
      </div>

      {result?.text ? (
        <p className="border-t border-slate-100 bg-slate-50 px-5 py-3 text-center text-sm text-slate-600" data-testid="today-result-text">
          {result.text}
        </p>
      ) : null}
    </div>
  );
}

const NAV_TILES = [
  { to: "/common-number", label: "Common Number", img: "/icons/tile_common.jpg", testid: "home-card-common" },
  { to: "/dream-number", label: "Dream Number", img: "/icons/tile_dream.jpg", testid: "home-card-dream" },
  { to: "/previous-result", label: "Previous Results", img: "/icons/tile_previous.jpg", testid: "home-card-previous" },
];

function NavTile({ tile }) {
  return (
    <Link
      to={tile.to}
      data-testid={tile.testid}
      aria-label={tile.label}
      className="block overflow-hidden rounded-2xl shadow-sm ring-1 ring-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
    >
      <img
        src={tile.img}
        alt={tile.label}
        loading="lazy"
        width="256"
        height="256"
        className="w-full aspect-square object-cover"
      />
    </Link>
  );
}

const FAQS = [
  { q: "Where can I check Night Teer Result Today?", a: "You can check the latest Night Teer Result right here on our homepage. The F/R and S/R results are displayed in a clear result card at the top of the page and are updated daily." },
  { q: "Where are the F/R and S/R results displayed?", a: "The First Round (F/R) and Second Round (S/R) results are shown side by side inside the main result card on the homepage, along with the result date and the official timings (F/R 11:10 PM, S/R 12:10 AM)." },
  { q: "Can I check previous Night Teer results?", a: "Yes. Visit the Previous Results page to browse past Night Teer results in a clean table showing Date, F/R and S/R. The newest results appear at the top and older results follow below." },
  { q: "Where can I find the Common Numbers?", a: "Common Numbers are published in the Common Number section and on the dedicated Common Number page. They include Direct, House and Ending numbers for informational purposes." },
  { q: "How quickly are the results updated?", a: "Results are published from our admin panel and appear on the website instantly, without any delay or need to refresh the whole site." },
];

function FaqItem({ item, open, onToggle, idx }) {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
      <button className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left" onClick={onToggle} data-testid={`faq-question-${idx}`}>
        <span className="font-semibold text-slate-900 text-sm sm:text-base">{item.q}</span>
        <ChevronDown size={20} className={`text-red-600 shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed" data-testid={`faq-answer-${idx}`}>{item.a}</div>}
    </div>
  );
}

export default function Home() {
  const [info, setInfo] = useState({ date: teerTodayDisplay(), result: null });
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(0);
  const [gameResults, setGameResults] = useState({});
  const [gamesDate, setGamesDate] = useState(teerTodayDisplay());
  const infoRef = useRef(null);

  useEffect(() => {
    let active = true;
    const loadGames = async () => {
      try {
        const { data } = await api.get("/results/today-all");
        if (!active) return;
        setGameResults(data.results || {});
        if (data.date) setGamesDate(data.date);
      } catch {}
    };
    loadGames();
    const gt = setInterval(loadGames, 60000);

    // Warm the previous-result cache for every game in the background so their
    // Previous pages open instantly when tapped.
    const prefetch = async () => {
      for (const g of GAMES) {
        if (sessionStorage.getItem(`pr_${g.slug}`)) continue;
        try {
          const { data } = await api.get(`/results?game=${g.slug}&page=1&limit=20`);
          sessionStorage.setItem(`pr_${g.slug}`, JSON.stringify({ items: data.items, pages: data.pages, total: data.total }));
        } catch {}
        if (!active) return;
      }
    };
    prefetch();

    return () => { active = false; clearInterval(gt); };
  }, []);

  useEffect(() => {
    let active = true;

    const load = async (isPoll) => {
      try {
        const { data } = await api.get("/results/today");
        if (!active) return;
        const prev = infoRef.current;
        const dateChanged = prev && prev.date !== data.date;
        // On the 8:00 PM IST session rollover, briefly show a loading state, then the new date.
        if (isPoll && dateChanged) {
          setLoading(true);
          await new Promise((r) => setTimeout(r, 900));
          if (!active) return;
        }
        infoRef.current = data;
        setInfo(data);
        setLoading(false);
      } catch {
        if (active) setLoading(false);
      }
    };

    load(false);
    const timer = setInterval(() => load(true), 60000);
    return () => { active = false; clearInterval(timer); };
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };

  return (
    <Layout>
      <Seo
        title="Night Teer Result Today | Shillong Night Teer Result Live FR SR"
        description="Check Night Teer Result Today with the latest FR and SR results. View Shillong Night Teer results, common numbers and previous results with fast daily updates."
        path="/"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <div className="home-poppins max-w-3xl mx-auto px-4 sm:px-6">
        {/* Main title */}
        <section className="pt-8 text-center">
          <h1 className="font-oswald text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Night Teer Result</h1>
          <div className="mx-auto mt-2 h-1 w-20 rounded-full bg-red-600" />
        </section>

        {/* Result card */}
        <section className="mt-5">
          <TodayResultCard date={info.date} result={info.result} loading={loading} />
        </section>

        {/* Three compact nav tiles */}
        <section className="mt-5 grid grid-cols-3 gap-2.5 sm:gap-4" data-testid="home-nav-cards">
          {NAV_TILES.map((t) => (
            <NavTile key={t.to} tile={t} />
          ))}
        </section>

        {/* Other Night Teer games */}
        <section className="mt-8" data-testid="home-games">
          <h2 className="font-oswald text-2xl font-bold tracking-tight text-slate-900 mb-1">More Night Teer Games</h2>
          <p className="text-sm text-slate-500 mb-4">Today's FR/SR result for each game. Tap for Previous or Common.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {GAMES.map((g) => {
              const r = gameResults[g.slug];
              return (
                <div key={g.slug} data-testid={`game-card-${g.slug}`} className="rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-white">
                  <Link to={`/result/${g.slug}`} className={`${g.color} flex items-center justify-between px-3 py-2.5 text-white`} data-testid={`game-title-${g.slug}`}>
                    <span className="game-name font-extrabold text-[15px] leading-tight tracking-wide">{g.name}</span>
                    <span className="text-[11px] opacity-90 whitespace-nowrap ml-2">FR {g.fr} · SR {g.sr}</span>
                  </Link>
                  <div className="flex items-center justify-center gap-1.5 border-b border-slate-100 bg-slate-50 py-2 text-slate-600" data-testid={`game-date-${g.slug}`}>
                    <Calendar size={13} className="shrink-0 text-red-600" />
                    <span className="text-xs font-semibold">{gamesDate}</span>
                  </div>
                  <div className="grid grid-cols-2 divide-x divide-slate-100 border-b border-slate-100">
                    <div className="py-3 text-center">
                      <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400">FR</span>
                      <span data-testid={`game-fr-${g.slug}`} className="font-oswald text-3xl font-bold text-red-600 leading-none">{r?.fr || "XX"}</span>
                    </div>
                    <div className="py-3 text-center">
                      <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400">SR</span>
                      <span data-testid={`game-sr-${g.slug}`} className="font-oswald text-3xl font-bold text-red-600 leading-none">{r?.sr || "XX"}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 divide-x divide-slate-200">
                    <Link to={`/previous-result/${g.slug}`} data-testid={`game-prev-${g.slug}`} className="py-2.5 text-center text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors">
                      Previous
                    </Link>
                    <Link to={`/common-number/${g.slug}`} data-testid={`game-common-${g.slug}`} className="py-2.5 text-center text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors">
                      Common
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* About */}
        <section className="mt-10">
          <h2 className="font-oswald text-2xl font-bold tracking-tight text-slate-900 mb-3">About Night Teer Result</h2>
          <div className="space-y-4 text-slate-600 leading-relaxed text-[15px]">
            <p>
              Night Teer is an evening archery-based number game popular in Meghalaya and the wider Shillong region.
              Results are announced in two rounds: the First Round (F/R) at 11:10 PM and the Second Round (S/R) at
              12:10 AM. Night Teer Result Today is an independent informational website created to make it simple and
              fast to check these results on any device.
            </p>
            <p>
              On this page you will always find today's F/R and S/R result displayed prominently at the top. As soon as
              a new result is officially announced and published from our panel, it appears here instantly. Every
              published result is also saved automatically to our Previous Results for easy reference later.
            </p>
            <p>
              Besides the daily result, we publish Common Number and Dream Number information for informational and
              entertainment purposes. You can also browse the Previous Results page to review past outcomes, with the
              most recent dates shown first.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-10">
          <h2 className="font-oswald text-2xl font-bold tracking-tight text-slate-900 mb-3">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {FAQS.map((item, i) => (
              <FaqItem key={i} idx={i} item={item} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
        </section>

        {/* Disclaimer */}
        <section className="mt-10 mb-4" data-testid="home-disclaimer">
          <div className="rounded-2xl bg-slate-900 p-5 sm:p-6 text-slate-300">
            <p className="text-sm leading-relaxed">
              <span className="font-bold text-white">Disclaimer:</span> Night Teer results are for informational
              purposes only. We do not guarantee any winning numbers or predictions. Common and dream numbers are for
              informational and entertainment purposes only. Please understand and follow the laws applicable in your
              location. Read our full{" "}
              <Link to="/disclaimer" className="font-semibold text-white underline">Disclaimer</Link>.
            </p>
          </div>
        </section>
      </div>
    </Layout>
  );
}
